import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Sparkle, X, PaperPlaneRight, CircleNotch, ArrowSquareOut } from "@phosphor-icons/react";
import { useProduct } from "@/context/ProductContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Persistent Ask MAI: floating entry point on every console workspace.
   Context-aware — sends product + current workspace with every question. */
export const GlobalAskMAI = () => {
  const { productId, product } = useProduct();
  const { pathname } = useLocation();
  const [openPanel, setOpenPanel] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { setSessionId(null); setMessages([]); }, [productId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (pathname.startsWith("/console/copilot")) return null;

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const r = await axios.post(`${API}/copilot/${productId}/sessions`);
    setSessionId(r.data.id);
    return r.data.id;
  };

  const send = async (e) => {
    e?.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const sid = await ensureSession();
      const context = `[Context — product: ${product?.name || productId}; workspace: ${pathname}]`;
      const resp = await fetch(`${API}/copilot/sessions/${sid}/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: `${context}\n${q}` }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) {
            acc += ev.delta;
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: acc };
              return c;
            });
          }
          if (ev.error) throw new Error(ev.error);
        }
      }
      if (!acc) throw new Error("empty");
    } catch (err) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Ask MAI could not answer just now — try again or open the full workspace." };
        return c;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="global-askmai-btn" onClick={() => setOpenPanel(true)}
        className="fixed bottom-6 right-6 z-40 bg-slate-900 text-white shadow-xl hover:bg-slate-700 px-4 py-3 flex items-center gap-2 text-sm font-semibold rounded-full">
        <Sparkle size={16} weight="fill" className="text-emerald-400" /> Ask MAI
      </button>
      {openPanel && (
        <div data-testid="global-askmai-panel" className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] bg-white border-l border-slate-200 shadow-2xl flex flex-col">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-emerald-600" />
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-sm">Ask MAI</p>
              <p className="text-[10px] text-slate-400 truncate">Context: {product?.name || productId} · {pathname.replace("/console", "") || "readiness"}</p>
            </div>
            <Link to="/console/copilot" data-testid="askmai-full-workspace" title="Open full workspace"
              className="text-slate-400 hover:text-slate-900 p-1.5"><ArrowSquareOut size={15} /></Link>
            <button data-testid="askmai-close" onClick={() => setOpenPanel(false)}
              className="text-slate-400 hover:text-slate-900 p-1.5"><X size={15} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-xs text-slate-400 space-y-2">
                <p>Ask anything about this product's evidence, markets or strategy — answers cite the knowledge graph.</p>
                {["What evidence can we reuse?", "What are the biggest gaps?", "What should we do first?"].map((s) => (
                  <button key={s} onClick={() => setText(s)}
                    className="block w-full text-left border border-slate-200 px-3 py-2 hover:bg-slate-50 text-slate-600">{s}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-xs leading-relaxed whitespace-pre-wrap px-3.5 py-2.5 max-w-[92%] ${m.role === "user" ? "ml-auto bg-slate-900 text-white" : "bg-slate-100 text-slate-800"}`}>
                {m.content}
              </div>
            ))}
            {busy && <p className="text-xs text-slate-400 flex items-center gap-2"><CircleNotch size={12} className="animate-spin" /> MAI is reading the knowledge graph…</p>}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-4 border-t border-slate-200 flex gap-2">
            <input data-testid="askmai-input" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Ask MAI…" className="flex-1 border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-slate-900" />
            <button data-testid="askmai-send" disabled={!text.trim() || busy}
              className="bg-slate-900 text-white px-4 disabled:opacity-40"><PaperPlaneRight size={15} /></button>
          </form>
        </div>
      )}
    </>
  );
};
